import { DateDefaultNowrap } from 'components/misc/DateDefaultNowrap.tsx';
import { RecordLink } from 'components/record-chip/RecordLink';
import type { DataTableColumnDef } from 'components/tables/data-table';
import { UltraBasicTable } from 'components/tables/UltraBasicTable';
import { Tooltip } from 'components/ui/tooltip';
import MoneyDisplay from 'components/util/MoneyDisplay';
import { getInvoiceTotalsAmounts } from 'pages/invoices/util/invoiceUtil';
import { ReactNode, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { useGetInvoiceTotalsQuery } from 'store/api/invoices';
import { ClassifierData } from 'types/classifiers';
import { LedgerTable } from 'pages/invoices/layout/record/general/LedgerTable';
import { InvoiceData, InvoicePayment, InvoicePaymentTypeCode } from 'types/invoices';
import { getClassifierText, t } from 'utils/getTranslations';
import { cn } from 'utils/cn';
import { toFixed } from 'utils/numberUtil';

/** A link in a table cell reads blue, where the cell's own rule would draw it as plain text. */
const LINKED_ENTITY = 'entity-link text-link! w-fit no-underline hover:underline';

/** The delete action takes the cell's link colours; only its size is its own. */
const DELETE_ACTION = 'entity-link w-fit text-[12px] no-underline hover:underline';

// only used internally in this file
enum InvoiceRevenueListItemType {
    INVOICE = 'INVOICE',
    INVOICE_PAYMENT = 'INVOICE_PAYMENT',
}

type InvoicePaymentRow = Partial<Omit<InvoicePayment, 'amount'>> & {
    type?: InvoiceRevenueListItemType;
    amount?: number | ReactNode;
};

type Props = {
    classifiers: Record<string, ClassifierData>;
    invoice: InvoiceData;
    deleteHandler: (id: number) => void;
    isInViewMode: boolean;
    isPrepaymentLinkedToDebitInvoice: boolean;
    /** The new design's card: a ledger table with its headers, the currency in the amount's. */
    isInCard?: boolean;
};

/** What a row's type names: the invoice itself, or the payment's type and the record the payment came from. */
function paymentTypeOf(
    row: InvoicePaymentRow,
    classifiers: Record<string, ClassifierData>
): { label: string; record?: ReactNode } {
    const { prepaidInvoice, invoicePaymentTypeCode, type, bankTransaction, correctionInvoice } = row;
    if (type === InvoiceRevenueListItemType.INVOICE) {
        return { label: t('invoices.invoice') };
    }
    // A payment by a correction invoice is that credit note.
    if (correctionInvoice) {
        return {
            label: t('invoices.general-tab.details.credit-note'),
            record: (
                <RecordLink
                    className={LINKED_ENTITY}
                    to={`/invoice/${correctionInvoice.id}`}
                    target={'_blank'}
                    label={correctionInvoice.invoiceNumber}
                    withOrigin
                />
            ),
        };
    }
    const record =
        invoicePaymentTypeCode === InvoicePaymentTypeCode.PREPAID_INVOICE && prepaidInvoice ? (
            <RecordLink
                className={LINKED_ENTITY}
                to={`/invoice/${prepaidInvoice.id}`}
                target={'_blank'}
                label={prepaidInvoice.invoiceNumber}
                withOrigin
            />
        ) : invoicePaymentTypeCode === InvoicePaymentTypeCode.BANK_TRANSFER && bankTransaction ? (
            <RecordLink
                className={LINKED_ENTITY}
                to={`/bank-transfers/${bankTransaction.id}`}
                target={'_blank'}
                label={bankTransaction.entryTransactionId}
                withOrigin
            />
        ) : undefined;
    return { label: getClassifierText(classifiers['INVOICE_PAYMENT_TYPE'], invoicePaymentTypeCode), record };
}

/** A direct payment's Delete; while the prepayment is linked to a debit invoice, off with its reason. */
function DeletePayment({
    row,
    isInViewMode,
    isPrepaymentLinkedToDebitInvoice,
    deleteHandler,
    className = DELETE_ACTION,
}: {
    row: InvoicePaymentRow;
    isInViewMode: boolean;
    isPrepaymentLinkedToDebitInvoice: boolean;
    deleteHandler: (id: number) => void;
    className?: string;
}) {
    const { id, invoicePaymentTypeCode } = row;
    if (invoicePaymentTypeCode !== InvoicePaymentTypeCode.DIRECT_PAYMENT || isInViewMode) {
        return null;
    }
    if (isPrepaymentLinkedToDebitInvoice) {
        return (
            <Tooltip content={t('invoices.tooltips.payment-delete-blocked-prepayment-linked')}>
                {/* focusable wrapper keeps the tooltip keyboard-reachable */}
                <span tabIndex={0}>
                    <span className={cn(className, 'pointer-events-none opacity-50')} aria-disabled>
                        {t('delete')}
                    </span>
                </span>
            </Tooltip>
        );
    }
    return (
        <button type="button" className={cn(className, 'cursor-pointer')} onClick={() => deleteHandler(id!)}>
            {t('delete')}
        </button>
    );
}

const InvoicePaymentsList = ({
    classifiers,
    invoice,
    deleteHandler,
    isInViewMode,
    isPrepaymentLinkedToDebitInvoice,
    isInCard = false,
}: Props) => {
    const { invoicePayments: originalInvoicePayments = [] } = invoice;
    const { data: invoiceTotals } = useGetInvoiceTotalsQuery(invoice.id);
    const paymentCurrency = invoiceTotals?.paymentCurrency.currencyTypeCode;

    const invoicePayments = useMemo(
        () => originalInvoicePayments.map((ip) => ({ ...ip, amount: ip.amount * -1 })),
        [originalInvoicePayments]
    );

    const { totalWithVatPaymentCurrency } = getInvoiceTotalsAmounts(invoiceTotals);

    const invoiceRow = useMemo(
        () => ({
            date: invoice.invoiceDate,
            amount: totalWithVatPaymentCurrency,
            type: InvoiceRevenueListItemType.INVOICE,
        }),
        [invoice.invoiceDate, totalWithVatPaymentCurrency]
    );

    const columns = useMemo<DataTableColumnDef<InvoicePaymentRow>[]>(
        () => [
            {
                accessorKey: 'date',
                cell: ({ getValue }) => {
                    const value = getValue<InvoicePaymentRow['date']>();
                    return value && <DateDefaultNowrap dateValue={value} />;
                },
            },
            {
                accessorKey: 'invoicePaymentTypeCode',
                cell: ({ cell }) => {
                    const { label, record } = paymentTypeOf(cell.row.original, classifiers);
                    if (cell.row.original.type === InvoiceRevenueListItemType.INVOICE) {
                        return <p className="text-body1 text-fg-secondary">{label}</p>;
                    }
                    return (
                        <div>
                            <p className="text-body1 text-fg-secondary">{label}</p>
                            {record ?? ''}
                        </div>
                    );
                },
            },
            {
                accessorKey: 'paymentNote',
                cell: ({ getValue }) => (
                    <p className="text-body1 text-fg-secondary">{getValue<InvoicePaymentRow['paymentNote']>() ?? ''}</p>
                ),
                meta: {
                    width: '30%',
                },
            },
            {
                id: '_',
                cell: ({ cell }) => (
                    <DeletePayment
                        row={cell.row.original}
                        isInViewMode={isInViewMode}
                        isPrepaymentLinkedToDebitInvoice={isPrepaymentLinkedToDebitInvoice}
                        deleteHandler={deleteHandler}
                    />
                ),
            },
            {
                accessorKey: 'amount',
                cell: ({ getValue }) => {
                    const value = getValue<InvoicePaymentRow['amount']>();
                    if (typeof value === 'number') {
                        return <MoneyDisplay amount={value} currency={paymentCurrency} displaySign />;
                    }
                    return value ?? '';
                },
            },
        ],

        [classifiers, deleteHandler, paymentCurrency, isInViewMode, isPrepaymentLinkedToDebitInvoice]
    );

    const remains = [...invoicePayments, invoiceRow]?.reduce(
        (accumulator, payment) => accumulator + toFixed(payment.amount, 2),
        0
    );

    const sumRow = useMemo(
        () => ({
            amount: (
                <p className="text-body1 text-fg-secondary inline-block whitespace-nowrap">
                    <FormattedMessage
                        id="invoices.remains"
                        values={{
                            amount: (
                                <span key="amount" className="text-body1 text-fg inline-block font-bold">
                                    <MoneyDisplay amount={remains} currency={paymentCurrency} displaySign />
                                </span>
                            ),
                        }}
                    />
                </p>
            ),
        }),
        [remains, paymentCurrency]
    );

    const dataRows = useMemo<InvoicePaymentRow[]>(
        () =>
            [invoiceRow, ...invoicePayments].sort((a, b) => {
                if (!a.date) return 1;
                if (!b.date) return -1;
                return new Date(a.date).getTime() - new Date(b.date).getTime();
            }),
        [invoicePayments, invoiceRow]
    );
    const rows = useMemo(() => [...dataRows, sumRow], [dataRows, sumRow]);

    if (isInCard) {
        return (
            <LedgerTable
                whatHeader={<FormattedMessage id="type" />}
                currency={paymentCurrency}
                remains={remains}
                hasActions={!isInViewMode}
                rows={dataRows.map((row, index) => {
                    const { label, record } = paymentTypeOf(row, classifiers);
                    return {
                        key: row.id ?? `invoice-${index}`,
                        date: row.date,
                        what: record ? (
                            <span className="flex flex-col items-start gap-0.5">
                                <span>{label}</span>
                                {record}
                            </span>
                        ) : (
                            label
                        ),
                        note: row.paymentNote,
                        action: (
                            <DeletePayment
                                row={row}
                                isInViewMode={isInViewMode}
                                isPrepaymentLinkedToDebitInvoice={isPrepaymentLinkedToDebitInvoice}
                                deleteHandler={deleteHandler}
                                className="text-primary text-caption w-fit font-medium no-underline hover:underline"
                            />
                        ),
                        amount: typeof row.amount === 'number' ? row.amount : 0,
                    };
                })}
            />
        );
    }

    return (
        <UltraBasicTable
            columns={columns}
            data={rows}
            className="[&_td]:px-1.5 [&_td]:py-3 [&_td:first-child]:pl-0 [&_td:last-child]:pr-0 [&_td:last-child]:text-right [&_td:nth-child(4)]:text-right"
        />
    );
};

export default InvoicePaymentsList;
